import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import logo from '../../assets/mpb_logo.png';
import { DONATE_LINK_PROPS } from '../../config/donate';
import './Home.css';

function Home() {
  return (
    <div className="home-page">
      <Helmet>
        <title>Mountain Pine Beetle Foundation | Protecting Our Forests</title>
        <meta name="description" content="Mountain Pine Beetle Foundation is a nonprofit dedicated to mitigating and preventing mountain pine beetle damage through early detection, preventive treatment, community education, and volunteer-driven forest conservation." />
        <meta name="keywords" content="mountain pine beetle, pine beetle prevention, Dendroctonus ponderosae, bark beetle, forest conservation, tree protection, beetle mitigation, wildfire prevention, nonprofit, volunteer, lodgepole pine, ponderosa pine" />
        <link rel="canonical" href="https://www.mountainpinebeetle.org/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.mountainpinebeetle.org/" />
        <meta property="og:title" content="Mountain Pine Beetle Foundation | Protecting Our Forests" />
        <meta property="og:description" content="A nonprofit dedicated to mitigating and preventing mountain pine beetle damage through early detection, preventive treatment, and community education." />
        <meta property="og:site_name" content="Mountain Pine Beetle Foundation" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://www.mountainpinebeetle.org/mpb_logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mountain Pine Beetle Foundation | Protecting Our Forests" />
        <meta name="twitter:description" content="A nonprofit dedicated to mitigating and preventing mountain pine beetle damage through early detection, preventive treatment, and community education." />
        <meta name="twitter:image" content="https://www.mountainpinebeetle.org/mpb_logo.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Mountain Pine Beetle Foundation",
            "description": "A nonprofit dedicated to mitigating and preventing mountain pine beetle damage through early detection, preventive treatment, and community education.",
            "url": "https://www.mountainpinebeetle.org/",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Mountain Pine Beetle Foundation",
              "url": "https://www.mountainpinebeetle.org"
            }
          }
        `}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay">
          <Container>
            <div className="hero-content">
              <span className="hero-eyebrow">Nonprofit Forest Conservation</span>
              <h1 className="hero-title">Protecting Our Forests from the <em>Mountain Pine Beetle</em></h1>
              <p className="hero-subtitle">
                Committed to prevention, education, and community action against mountain pine beetle devastation across western North America.
              </p>
              <div className="hero-buttons">
                <Button
                  variant="light"
                  size="lg"
                  as={Link}
                  to="/learn-more"
                  className="me-2 mb-2"
                >
                  Learn More
                </Button>
                <a {...DONATE_LINK_PROPS} className="btn btn-donate btn-lg me-2 mb-2">
                  Donate
                </a>
                <Button
                  variant="outline-light"
                  size="lg"
                  as={Link}
                  to="/contact"
                  className="mb-2"
                >
                  Get Involved
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="stats-section">
        <Container>
          <Row>
            <Col xs={6} md={3}>
              <div className="stat-item fade-in-up fade-in-up-d1">
                <div className="stat-number color-rust">30M+</div>
                <div className="stat-label">Acres Affected<sup className="ref-sup"><a href="#ref-1">[1]</a></sup></div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-item fade-in-up fade-in-up-d2">
                <div className="stat-number color-forest">20+</div>
                <div className="stat-label">Years of Epidemic<sup className="ref-sup"><a href="#ref-2">[2]</a></sup></div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-item fade-in-up fade-in-up-d3">
                <div className="stat-number color-amber">$6B+</div>
                <div className="stat-label">Economic Impact<sup className="ref-sup"><a href="#ref-3">[3]</a></sup></div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-item fade-in-up fade-in-up-d4">
                <div className="stat-number color-navy">12</div>
                <div className="stat-label">States Impacted<sup className="ref-sup"><a href="#ref-4">[4]</a></sup></div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Brand Section */}
      <section className="brand-section py-5">
        <Container>
          <Row className="align-items-center g-5">
            <Col xs={12} md={5} className="text-center">
              <img src={logo} alt="Mountain Pine Beetle Foundation" className="brand-logo" />
            </Col>
            <Col xs={12} md={7}>
              <span className="brand-eyebrow">Who We Are</span>
              <h2 className="brand-heading">Mountain Pine Beetle Foundation</h2>
              <p className="brand-text">
                We are a nonprofit dedicated to safeguarding western North America's forests from the devastating mountain pine beetle epidemic through science-driven prevention, community mobilization, and public education.
              </p>
              <div className="brand-highlights">
                <div className="brand-highlight-item">
                  <span className="brand-dot brand-dot-forest"></span>
                  Prevention &amp; Treatment
                </div>
                <div className="brand-highlight-item">
                  <span className="brand-dot brand-dot-amber"></span>
                  Research &amp; Monitoring
                </div>
                <div className="brand-highlight-item">
                  <span className="brand-dot brand-dot-rust"></span>
                  Community Education
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Threat Section */}
      <section className="threat-section py-5">
        <Container>
          <h2 className="text-center mb-2">The Threat We Face</h2>
          <p className="text-center mb-5 mx-auto" style={{ maxWidth: '540px' }}>
            Understanding the scale and urgency of the mountain pine beetle epidemic.
          </p>
          <Row className="g-4">
            <Col xs={12} md={6} lg={3}>
              <Card className="threat-card h-100 border-0">
                <Card.Body className="p-4">
                  <div className="threat-icon mb-3">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L4 8h2l-3 5h2l-3 6h8v3h4v-3h8l-3-6h2l-3-5h2L12 2zm0 2.5L16.5 8h-1.8l2.5 4h-1.5l2.3 4H6l2.3-4H6.8l2.5-4H7.5L12 4.5z"/>
                    </svg>
                  </div>
                  <Card.Title>Millions of Acres Lost</Card.Title>
                  <Card.Text className="text-muted small">
                    Mountain pine beetles have destroyed tens of millions of acres of forest across North America, devastating entire ecosystems.<sup className="ref-sup"><a href="#ref-1">[1]</a></sup>
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Card className="threat-card h-100 border-0">
                <Card.Body className="p-4">
                  <div className="threat-icon mb-3">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
                    </svg>
                  </div>
                  <Card.Title>Communities Impacted</Card.Title>
                  <Card.Text className="text-muted small">
                    Forest-dependent communities face economic hardship, increased wildfire risk, and loss of recreational and cultural resources.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Card className="threat-card h-100 border-0">
                <Card.Body className="p-4">
                  <div className="threat-icon mb-3">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                  </div>
                  <Card.Title>Climate Driven</Card.Title>
                  <Card.Text className="text-muted small">
                    Warmer winters allow beetle populations to thrive at higher elevations and latitudes, expanding the range of destruction.<sup className="ref-sup"><a href="#ref-2">[2]</a></sup>
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Card className="threat-card h-100 border-0">
                <Card.Body className="p-4">
                  <div className="threat-icon mb-3">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z"/>
                    </svg>
                  </div>
                  <Card.Title>Wildfire Risk</Card.Title>
                  <Card.Text className="text-muted small">
                    Dead standing trees from beetle kill create massive fuel loads, dramatically increasing the risk of catastrophic wildfires.<sup className="ref-sup"><a href="#ref-5">[5]</a></sup>
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Donate Band */}
      <section className="donate-band py-5">
        <Container>
          <Row className="align-items-center g-4">
            <Col xs={12} lg={8}>
              <span className="donate-band-eyebrow">Fund the Fight</span>
              <h2 className="donate-band-title mb-2">Your Donation Protects Forests</h2>
              <p className="donate-band-text mb-0">
                Help fund tree treatment, beetle monitoring, and community education programs &mdash; every contribution accelerates our work to slow the epidemic.
              </p>
            </Col>
            <Col xs={12} lg={4} className="text-lg-end">
              <a {...DONATE_LINK_PROPS} className="btn btn-donate btn-lg px-4">
                Donate Now
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Mission Section */}
      <section className="mission-section py-5">
        <Container>
          <Row className="align-items-center g-5">
            <Col xs={12} lg={6}>
              <h2 className="mb-3">Our Mission</h2>
              <p className="mb-3">
                Mountain Pine Beetle Foundation is a nonprofit organization dedicated to protecting forests through proactive prevention, community education, and coordinated mitigation efforts. We work with landowners, communities, and forestry professionals to combat the mountain pine beetle epidemic.
              </p>
              <ul className="mission-list mb-4">
                <li>Early detection and monitoring programs</li>
                <li>Preventive tree treatment and forest management</li>
                <li>Community education and outreach</li>
                <li>Research partnerships and advocacy</li>
              </ul>
              <Button variant="primary" size="lg" as={Link} to="/contact">
                Get in Touch
              </Button>
            </Col>
            <Col xs={12} lg={6}>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Card className="mission-card h-100 border-0 text-center p-3">
                    <Card.Body>
                      <div className="mission-icon mb-3 mx-auto">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        </svg>
                      </div>
                      <Card.Title className="fs-6">Early Detection</Card.Title>
                      <Card.Text className="text-muted small">Monitor forests for early signs of beetle infestation to enable rapid response.</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="mission-card h-100 border-0 text-center p-3">
                    <Card.Body>
                      <div className="mission-icon mb-3 mx-auto">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 4h-1V2H7v2H6c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h1v6H4v2h16v-2h-3v-6h1c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM8 4h8v2H8V4zm10 6h-2V8h2v2zm-4 8h-4v-6h4v6z"/>
                        </svg>
                      </div>
                      <Card.Title className="fs-6">Tree Treatment</Card.Title>
                      <Card.Text className="text-muted small">Preventive treatments protect vulnerable trees from beetle attack.</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="mission-card h-100 border-0 text-center p-3">
                    <Card.Body>
                      <div className="mission-icon mb-3 mx-auto">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
                        </svg>
                      </div>
                      <Card.Title className="fs-6">Education</Card.Title>
                      <Card.Text className="text-muted small">Community workshops and resources to spread awareness and prevention knowledge.</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="mission-card h-100 border-0 text-center p-3">
                    <Card.Body>
                      <div className="mission-icon mb-3 mx-auto">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                        </svg>
                      </div>
                      <Card.Title className="fs-6">Community Action</Card.Title>
                      <Card.Text className="text-muted small">Coordinated volunteer efforts to protect forests at the local level.</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Prevention Methods Section */}
      <section className="prevention-section py-5">
        <Container>
          <h2 className="text-center mb-2">How You Can Help</h2>
          <p className="text-center mb-5 mx-auto" style={{ maxWidth: '480px' }}>
            Every action counts in the fight to protect our forests.
          </p>
          <Row className="g-4">
            <Col xs={6} md={3}>
              <div className="prevention-item text-center">
                <div className="prevention-icon mb-3 mx-auto">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                </div>
                <h6>Report Damage</h6>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="prevention-item text-center">
                <div className="prevention-icon mb-3 mx-auto">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                </div>
                <h6>Volunteer</h6>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <a {...DONATE_LINK_PROPS} className="prevention-link text-decoration-none text-reset">
                <div className="prevention-item text-center">
                  <div className="prevention-icon mb-3 mx-auto">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.94s4.18 1.36 4.18 3.88c0 1.85-1.37 2.84-3.12 3.16z"/>
                    </svg>
                  </div>
                  <h6>Donate</h6>
                </div>
              </a>
            </Col>
            <Col xs={6} md={3}>
              <div className="prevention-item text-center">
                <div className="prevention-icon mb-3 mx-auto">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z"/>
                  </svg>
                </div>
                <h6>Spread the Word</h6>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <Row className="align-items-center">
            <Col xs={12} lg={6} className="mb-4 mb-lg-0">
              <h2>Ready to Make a Difference?</h2>
              <p className="mb-0">
                Whether you're a landowner looking to protect your trees, a community member wanting to volunteer, or someone who wants to learn more about the mountain pine beetle threat, we're here to help.
              </p>
            </Col>
            <Col xs={12} lg={6} className="text-lg-end">
              <a {...DONATE_LINK_PROPS} className="btn btn-donate btn-lg me-2 mb-2">
                Donate
              </a>
              <Button
                variant="light"
                size="lg"
                as={Link}
                to="/contact"
                className="me-2 mb-2"
              >
                Contact Us
              </Button>
              <Button
                variant="outline-light"
                size="lg"
                as={Link}
                to="/learn-more"
                className="mb-2"
              >
                Learn More
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* References Footnote */}
      <section className="references-section py-4">
        <Container>
          <h6 className="references-heading mb-3">Sources &amp; References</h6>
          <ol className="references-list">
            <li id="ref-1">USDA Forest Service. "Bark Beetle Outbreaks in Western North America." <em>Forest Health Protection</em>, fs.usda.gov.</li>
            <li id="ref-2">Bentz, B.J. et al. (2010). "Climate Change and Bark Beetles of the Western United States and Canada." <em>BioScience</em>, 60(8), 602–613.</li>
            <li id="ref-3">Natural Resources Canada. "Mountain Pine Beetle." <em>NRCan Forest Insects &amp; Disturbances</em>, nrcan.gc.ca.</li>
            <li id="ref-4">USDA Forest Service. "Major Forest Insect and Disease Conditions in the United States." <em>Forest Health Protection</em>, fs.usda.gov.</li>
            <li id="ref-5">Hicke, J.A. et al. (2012). "Effects of Bark Beetle-Caused Tree Mortality on Wildfire." <em>Forest Ecology and Management</em>, 271, 81–90.</li>
          </ol>
        </Container>
      </section>
    </div>
  );
}

export default Home;
